/**
 * Employee tracking mode — mirrors UsersTableComponent.getModeTooltip() /
 * getModeIcon() in users-table.component.ts. Hidden and Admin Controlled are
 * separate modes with different downgrade remediation flows.
 */
export type EmployeeTrackingMode =
  | 'hidden'
  | 'admin_controlled'
  | 'employee_controlled'
  | 'unknown';

export function isTruthyModeSetting(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

export function getEmployeeSettingsRecord(user: any): Record<string, unknown> {
  const settings = user?.meta?.settings;
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return {};
  }
  return settings as Record<string, unknown>;
}

export function getAgentLabel(user: any): string {
  return String(user?.appInfo?.label ?? '').trim().toUpperCase();
}

export function getEmployeeTrackingMode(
  user: any,
  globalSettings: Record<string, unknown>
): EmployeeTrackingMode {
  const label = getAgentLabel(user);
  const settings = getEmployeeSettingsRecord(user);

  if (label === 'S') {
    return 'hidden';
  }

  if (label === 'R') {
    return 'admin_controlled';
  }

  if (label === 'G') {
    const autoTracking = settings.ss_automatic_tracking !== undefined
      ? settings.ss_automatic_tracking
      : globalSettings.ss_automatic_tracking;
    return isTruthyModeSetting(autoTracking)
      ? 'admin_controlled'
      : 'employee_controlled';
  }

  const versionType = getWpVersionType(user);
  if (versionType === 'silent') {
    return 'hidden';
  }
  if (versionType === 'restricted') {
    return 'admin_controlled';
  }

  // Fallback for general agents without label

  const autoTracking = settings.ss_automatic_tracking !== undefined
    ? settings.ss_automatic_tracking
    : globalSettings.ss_automatic_tracking;
  return isTruthyModeSetting(autoTracking)
    ? 'admin_controlled'
    : 'employee_controlled';

  return 'unknown';
}

export function isHiddenTrackingMode(
  user: any,
  globalSettings: Record<string, unknown>
): boolean {
  return getEmployeeTrackingMode(user, globalSettings) === 'hidden';
}

export function isAdminControlledTrackingMode(
  user: any,
  globalSettings: Record<string, unknown>
): boolean {
  return getEmployeeTrackingMode(user, globalSettings) === 'admin_controlled';
}

export function collectAdminControlModeDetails(
  user: any,
  globalSettings: Record<string, unknown>
): string[] {
  const label = getAgentLabel(user);
  const settings = getEmployeeSettingsRecord(user);
  const versionType = getWpVersionType(user);

  if (label === 'R' || versionType === 'restricted') {
    return ['Admin Controlled agent installed'];
  }

  if (settings.ss_automatic_tracking !== undefined) {
    if (isTruthyModeSetting(settings.ss_automatic_tracking)) {
      return ['Auto Tracking (Restricted Mode) enabled'];
    }
  } else if (isTruthyModeSetting(globalSettings.ss_automatic_tracking)) {
    return ['Inheriting workspace Auto Tracking (Restricted Mode)'];
  }

  return ['Inheriting workspace Auto Tracking (Restricted Mode)'];
}

function getWpVersionType(user: any): string {
  const candidates = [user?.version_type, user?.meta?.version_type, user?.meta?.agent_version_type];
  for (const candidate of candidates) {
    const normalized = String(candidate ?? '').trim().toLowerCase();
    if (normalized === 'silent' || normalized === 'hidden' || normalized === 's') {
      return 'silent';
    }
    if (normalized === 'restricted' || normalized === 'r') {
      return 'restricted';
    }
    if (normalized === 'general' || normalized === 'g') {
      return 'general';
    }
  }
  return '';
}
